import React, { useRef, useState, useLayoutEffect } from 'react';
import classnames from 'classnames';
import startCase from 'lodash/startCase';
import { useSelector } from 'react-redux';

import {
  getItemDisplayName,
  isAddressItem,
  isPOIItem,
} from '../../../../common/utils/helpers/item';
import { selectStyleSelectorShown } from '../../../../common/store/ui';
import WizardLayout from '../../../../common/components/wizard-layout/WizardLayout';
import Button from '../../../../common/components/controls/button/Button';
import Responsive, {
  useMobile,
} from '../../../../common/components/responsive/Responsive';

import './ItemDetails.scss';

const properties = [
  {
    title: 'Address',
    getter: (item) => item.properties.address || item.properties.description,
  },
];

const getProperties = (item) =>
  properties
    .map(({ title, getter }) => ({ title, value: getter(item) }))
    .filter(({ value }) => value !== undefined && value !== '');

export interface ItemDetailsProps {
  className?: string;
  item;
  onEdit;
  onCloseOrRemove;
}

const ItemDetails = ({
  className,
  item,
  onEdit,
  onCloseOrRemove,
}: ItemDetailsProps) => {
  const isMobile = useMobile();

  const [minHeight, setMinHeight] = useState<number>();
  const [maxHeight, setMaxHeight] = useState<number>();

  const layoutRef = useRef<HTMLElement>();
  useLayoutEffect(() => {
    if (!isMobile) {
      setMinHeight(undefined);
      setMaxHeight(undefined);
      return;
    }

    if (layoutRef.current) {
      const layoutHeader = layoutRef.current.getElementsByClassName(
        'wizard-layout__header',
      )[0] as HTMLElement;
      const layoutHeaderHeight = layoutHeader.offsetHeight;

      const buttons = layoutRef.current.getElementsByClassName(
        'item-details__buttons',
      )[0] as HTMLElement;
      const buttonsHeight = buttons?.offsetHeight || 0;

      const properties = layoutRef.current.getElementsByClassName(
        'item-details__properties',
      )[0];
      const propertiesHeight = properties?.scrollHeight || 0;

      setMinHeight(layoutHeaderHeight + buttonsHeight);
      setMaxHeight(layoutHeaderHeight + buttonsHeight + propertiesHeight);
    }
  }, [isMobile, item]);

  const styleSelectorShown = useSelector(selectStyleSelectorShown);
  if (isMobile && styleSelectorShown) {
    return null;
  }

  const properties = getProperties(item).map(({ title, value }) => (
    <li key={title} className="item-details__property">
      <h3 className="item-details__property-title">{title}:</h3>
      <span className="item-details__property-value">{value}</span>
    </li>
  ));

  const removable = isPOIItem(item);
  const editable = isAddressItem(item) || isPOIItem(item);

  const detailsButtons =
    editable || removable ? (
      <div className="item-details__buttons">
        {(editable || removable) && (
          <div className="item-details__buttons-row">
            {editable && (
              <Button
                className="item-details__button"
                appearance="primary"
                onClick={onEdit}
              >
                Suggest an edit
              </Button>
            )}
            {removable && (
              <Button
                className="item-details__button item-details__button-close-or-remove"
                onClick={onCloseOrRemove}
              >
                Close or remove
              </Button>
            )}
          </div>
        )}
      </div>
    ) : null;

  return (
    <WizardLayout
      className={classnames('item-details', className)}
      ref={layoutRef}
      title={getItemDisplayName(item)}
      description={
        item.properties.poi_category &&
        item.properties.poi_category
          .map((cat) => startCase(cat.trim()))
          .join(', ')
      }
      hasOuterBackButton={isMobile}
      hasCloseButton={isMobile}
      resizable={isMobile}
      resizeOptions={{ minHeight, maxHeight }}
    >
      <Responsive mobile={true}>{detailsButtons}</Responsive>
      {properties.length > 0 && (
        <ul className="item-details__properties">{properties}</ul>
      )}
      <Responsive mobile={false}>{detailsButtons}</Responsive>
    </WizardLayout>
  );
};

export default ItemDetails;
